import { RestClientErrorType } from '@superbet-group/web.lib.rest';
import {
    AuthenticationError,
    failure,
    MalformedDataError,
    Result,
    success,
    ValidationError,
} from '@superbet-group/web.lib.utilities';
import { sha256 } from '@src/app/utils/sha256';
import {
    pick as _pick,
    isEmpty as _isEmpty,
    throttle,
} from 'lodash';

import {
    AccountRecoveryToken,
    LoginPlayerAlternativeChannelResponse,
} from '@accountFederation/modules/accountRecovery/types';
import User, { Session } from '@core/models/user/User';
import RestService from '@core/services/authentication/_private/RestService';
import { registerAttributionHistory } from '@core/services/marketing/_private/constants';
import RegisterInput from '@countryModels/authentication/RegisterInput';
import { activateAccount as countryActivateAccount } from '@accountCountries/registration/countryAuthentication';
import { shellLocalConfig } from '@src/config/shell/localConfig';
import { LoginCredentials } from '@src/core/models/authentication/LoginCredentials';
import {
    ErrorCode,
    getTranslatedErrorCode,
    getTranslatedReasonCode,
    ReasonCodes,
} from '@accountFederation/utils/mappers/translationsCodes';
import * as NetworkUtils from '@utils/network/networkHelpers';

import {
    CustomSessionAttributes,
    HrPlayerDataDTO,
    LoginSuccessData,
    RegisterResponse,
    SendMfaCodePayload,
    SendMfaCodeResponse,
    VerifyMfaCodePayload,
    VerifyMfaCodeResponse,
    SessionStats,
} from './_private/Types';
import { getDetails } from './helpers';
import {
    LoginError,
    ExploitLogoutError,
} from './types';
import { marketingSourceService } from '../marketing/MarketingSourceService';
import { handleOnRegistrationError } from './_private/helpers';
import { Analytics as AnalyticsSDK } from '@superbet-group/web.analytics.sdk';
import { getDarklyExpId } from '../../../app/shared/featureFlags/isAlternativeRegistrationEnabled';
import { logger } from '@src/app/logger';
import {
    AnalyticsAction,
    IdentityProvider,
} from '@superbet-group/web.types';
import { getAccountConfig } from '@accountFederation/config/accountConfig';
import { isIdCardVerificationPending } from '@models/user/UserDataService';
import {
    KycDocStatus,
    KycStatus,
} from '@accountFederation/modules/idCardVerification/getid/types';
import MfaRequiredError from '@core/errors/user/MfaRequiredError';
import { MultiLoginResponse } from '@app/multiLogin/types';

export default class AuthenticationService {
    public restService: RestService = new RestService();
    public activateAccount: (token: string, multiLoginDestination: string | null)
        => Promise<User & MultiLoginResponse> | Promise<null>;

    constructor() {
        this.activateAccount = countryActivateAccount.bind(this);
    }

    /**
     * @throws {Error}
     */
    public async register(registerInput: RegisterInput): Promise<RegisterResponse> {
        const setFieldErrorIfExist = (name: string, fieldErrors: any) => {
            if (response.fieldErrors[name]) {
                const errorMessage = shellLocalConfig.isUsingErrorCodesTranslations
                    ? getTranslatedErrorCode(response.errors.find((error: ErrorCode) => error.field === name))
                    : response.fieldErrors[name];
                // eslint-disable-next-line no-param-reassign
                fieldErrors[name] = [errorMessage];
            }
        };

        // INFO : Marketing Attribution data that goes through custompayload object are not yet interpreted by Betler upon registration but functionality is prepared in case Betler will implement this in the future
        const response = await this.restService.register({
            ...registerInput,
            ...(marketingSourceService.getMarketingData() || marketingSourceService.getPostViewData()),
            ...marketingSourceService.getAssembledMarketingAttributionData(registerAttributionHistory),
        });

        if (response.error) {
            handleOnRegistrationError(response, registerInput, setFieldErrorIfExist);
        }

        const hashEmail = await sha256(registerInput.email);

        AnalyticsSDK.getInstance().log('Registration_Step3', {
            email: registerInput.email,
            username: registerInput.username,
            hashEmail: hashEmail,
            userId: response.data.PlayerId,
            userUuid: response.data.CustomerId || '',
            registrationMethod: AnalyticsAction.Classic,
            bonus: registerInput.couponCode || '',
            registrationCounty: registerInput.county || registerInput.countryOfResidence || '',
            registrationCity: registerInput.city || '',
            mobile: registerInput.phone || '',
            address: registerInput.address || '',
            firstName: registerInput.firstName,
            lastName: registerInput.lastName,
            darklyExpId: getDarklyExpId(),
        });

        return response;
    }

    public async verifyPlayerDetails(playerData: HrPlayerDataDTO): Promise<any> {
        const response = await this.restService.verifyPlayerDetails(playerData);
        if (response && response.error) {
            // TODO: Check what kind of error should be thrown from here
            throw new ValidationError({ firstName: ['check your identification info: name, surname, date of birth and oib'] });
        }
    }

    public async isRegisterFieldValid(key: string, value: unknown): Promise<any> {
        // BEWARE: isRegisterFieldValid has cancelable request!!
        const response = await this.restService.isRegisterFieldValid(key, value);
        if (response && response.error) {
            const errorMessage = shellLocalConfig.isUsingErrorCodesTranslations
                ? getTranslatedErrorCode(response.errors.find((error: ErrorCode) => error.field === key))
                : response.fieldErrors[key];

            throw new ValidationError({
                [key]: [errorMessage],
            });
        }
    }

    public async validateRegistrationData(regData: Record<string, any>): Promise<any> {
        const response = await this.restService.validateRegistrationData(regData);
        if (response && response.error) {
            const fieldErrors: Record<string, string[]> = {};

            Object.keys(regData).filter((key) => response.fieldErrors[key]).forEach((key: string) => {
                fieldErrors[key] = [response.fieldErrors[key]];
            });
            throw new ValidationError(fieldErrors);
        }
    }

    /**
     * @throws {RequestError}
     * @throws {Error}
     */
    public async login(credentials: LoginCredentials, multiLoginDestination: string | null): Promise<User & MultiLoginResponse> {
        const response = await this.restService.login(credentials, multiLoginDestination);

        if (response.error) {
            AnalyticsSDK.getInstance().log('Login_Error');

            throw new LoginError(response);
        }

        return {
            redirect: response.redirect,
            ...this.createUser(response.data) as any, // TODO remove any: "Reference User Model",
            ...(response.data.statusCode === 'MFARequired' && {
                mfaPayload: {
                    playerId: response.data.userId,
                    secureToken: response.data.sessionId.split('|')[0],
                    purpose: 'authentication',
                },
            }),
        };
    }

    /**
     * @throws {AuthenticationError}
     * @throws {RequestError}
     * @throws {Error}
     */
    public async getSession(): Promise<User> {
        const response = await this.restService.getSession();

        if (response.error) {
            throw new AuthenticationError();
        }

        response.data.sessionId = `${response.data.sessionId}|${response.data.userId}`;
        return this.createUser(response.data as any) as any; // TODO remove any: "Reference User Model"
    }

    public async checkLoginSession(shouldReturnReasonCode?: boolean): Promise<Result<Session, null | string> & { shouldLogout?: boolean }> {
        try {
            const response = await this.restService.checkLoginSession();

            if (response.error) {
                return {
                    ...failure(shouldReturnReasonCode ? getTranslatedReasonCode(response.reasonCode) : null),
                    shouldLogout: true,
                };
            }

            const data = response.data.LogonSession;
            return success({
                userId: data.PlayerId,
                sessionId: data.SessionToken,
                logonTime: new Date(data.LogonTime).getTime(),
            });
        } catch (error) {
            if (isBetlerAuthenticationError(error)) {
                // Case for when we get 401 without any response body (Betler)
                return {
                    ...failure(shouldReturnReasonCode ? getTranslatedReasonCode(ReasonCodes.SessionExpired) : null),
                    shouldLogout: true,
                };
            } else {
                // Unexpected, let's log this
                logger.error(error as Error);
                return failure(null);
            }
        }
    }

    public throttledCheckLoginSession = throttle(this.checkLoginSession, 10 * 1000);

    /**
     * @throws {AuthenticationError}
     * @throws {RequestError}
     * @throws {Error}
     */
    public async logout(): Promise<any> {
        const response = await this.restService.logout();

        if (response.error) {
            if (response.errorCode === 'PlayerSessionCheckFailed') {
                throw new AuthenticationError();
            }
            if (response.errorCode === 'SubmittingTicket') {
                throw new ExploitLogoutError(response);
            }
        }
    }

    public async updatePassword(oldPassword: string, newPassword: string, otp?: string): Promise<any> {
        const response = await this.restService.updatePassword(oldPassword, newPassword, otp);

        if (response.error) {
            const errors: any = {};

            if (response.errorCode && response.errorCode === 'InvalidOldCred') {
                errors.oldPassword = ['wrong current password'];
            }

            if (response.errorCode && response.errorCode === 'MfaVerificationFailed') {
                // TODO: Create a MFA / security error
                throw new AuthenticationError();
            }

            if (response.fieldErrors && response.fieldErrors.newpassword) {
                errors.newPassword = [response.fieldErrors.newpassword];
            }

            if (!_isEmpty(errors)) {
                throw new ValidationError(errors);
            } else {
                throw NetworkUtils.getRequestError(response);
            }
        }
    }

    public async forgotPasswordRequest(email: string): Promise<any> {
        const response = await this.restService.forgotPasswordRequest(email);

        if (response.error) {
            if (response.errorCode === 'PlayerDataNotCorrect') {
                throw new ValidationError({
                    email: ['E-mail not found'],
                });
            } else if (response.errorCode === 'MFARequired') {
                throw new MfaRequiredError();
            } else {
                throw NetworkUtils.getRequestError(response);
            }
        }
    }

    public async sendPlayerLoginNotificationRequest(email: string): Promise<void> {
        const response = await this.restService.sendPlayerLoginNotificationRequest(email);

        if (response.ErrorMessage) {
            throw NetworkUtils.getRequestError(response);
        }
    }

    public loginPlayerAlternativeChannelRequest(token: AccountRecoveryToken): Promise<LoginPlayerAlternativeChannelResponse> {
        return this.restService.loginPlayerAlternativeChannelRequest(token);
    }

    public async changeForgottenPassword(token: string, password: string, username: string, hasOtpId: boolean | undefined = false): Promise<any> {
        const response = await this.restService.changeForgottenPassword(token, password, username, hasOtpId);

        if (response.error) {
            if (response.fieldErrors && response.fieldErrors.newPassword) {
                const errorMessage = shellLocalConfig.isUsingErrorCodesTranslations
                    ? getTranslatedErrorCode(response.errors.find((error: ErrorCode) => error.field && error.code))
                    : response.fieldErrors.newPassword;

                throw new ValidationError({
                    password: [errorMessage],
                });
            } else if (response.errorCode === 'UsernameInPasswordPresent') {
                throw new ValidationError({
                    password: ['auth.password.validation.distinctFromUsername'],
                });
            } else if (response.errorCode === 'ChangePasswordTokenNotFound') {
                throw new MalformedDataError(response.notice);
            } else {
                throw NetworkUtils.getRequestError(response);
            }
        }
    }

    // eslint-disable-next-line class-methods-use-this
    private createUser(data: LoginSuccessData) {
        const user = _pick(
            data,
            [
                'sessionId',
                'welcomeBonus',
                'bonusAbuser',
                'balance',
                'currency',
                'externalUUID',
                'csrfToken',
                'isFoundInRetail',
                'hasWallet',
                'retailPlayerId',
                'userUuid',
                ...(getAccountConfig().hasGenderAndNationalityRequirement
                    ? [
                        'nationality',
                        'gender',
                    ]
                    : []
                ),
            ],
        ) as Pick<User, 'sessionId' | 'welcomeBonus' | 'bonusAbuser' | 'balance' | 'currency' | 'externalUUID' | 'csrfToken' | 'isFoundInRetail' | 'hasWallet' | 'retailPlayerId' | 'userUuid'>;

        const expandedUser = {
            ...user,
            isVerifiedIdCard: data.kycChecked,
            isTempAccount: !data.kycChecked,
            ...(data.reKycStatus ? { reKycStatus: data.reKycStatus } : {}),
            ...(data.peselNumber ? { peselNumber: data.peselNumber } : {}),
            ...(data.documentNumber ? { documentNumber: data.documentNumber } : {}),
        };

        return {
            ...expandedUser,
            ...getDetails(data as any), // TODO remove any: "Reference User Model"
        };
    }

    public async getIdentityProviderUrl(provider: IdentityProvider, redirectUrl?: string): Promise<string> {
        const response = await this.restService.getIdentityProviderUrl(provider, redirectUrl);

        if (response.error) {
            throw NetworkUtils.getRequestError(response);
        }

        return response.data.Url;
    }

    public async setSessionAttributes(attributes: CustomSessionAttributes): Promise<any> {
        const response = await this.restService.setSessionAttributes(attributes);

        if (response.error) {
            throw NetworkUtils.getRequestError(response);
        }

        return true;
    }

    public async getSessionAttributes(): Promise<CustomSessionAttributes> {
        const response = await this.restService.getSessionAttributes();

        if (response.error) {
            throw NetworkUtils.getRequestError(response);
        }

        return response;
    }

    public async getKYCMojeIdResponse(conversationId: string): Promise<any> {
        const response = await this.restService.getKYCMojeIdResponse(conversationId);

        if (response.error) {
            if (response.fieldErrors) {
                throw new ValidationError({ general: [Object.values(response.fieldErrors as string[])[0].toString()] });
            } else {
                throw NetworkUtils.getRequestError(response);
            }
        }

        return response;
    }

    public async getKycDetails(user?: User): Promise<any> {
        const { data, ...rest } = await this.restService.getKycDetails();
        const response = data ? { ...data, ...rest } : rest;

        if (response.error) {
            throw NetworkUtils.getRequestError(response);
        }
        if (getAccountConfig().useKycLocalStoragePendingState) {
            const statusChanged = response.status !== user?.isVerifiedIdCard;
            response.isStatusPending = statusChanged ? isIdCardVerificationPending(user!) : user!.kycDetails?.isStatusPending;
        } else {
            response.hasDocumentInReview = !!response.docs?.some((doc: { status: string; }) => doc.status === KycDocStatus.NeedsReview);
            response.isStatusPending = response.status !== KycStatus.Verified && response.hasDocumentInReview;
        }
        return response;
    }

    public async getMfaStatus(params: unknown): Promise<any> {
        const response = await this.restService.getMfaStatus(params);

        if (response.error) {
            throw NetworkUtils.getRequestError(response);
        }

        return response;
    }

    public async verifyOtpCode(payload: VerifyMfaCodePayload): Promise<VerifyMfaCodeResponse> {
        const response = await this.restService.verifyOtpCode(payload);

        if (response.error) {
            if (
                response.errorCode === 'InvalidToken'
                || response.errorCode === 'MaxMFAAttempts'
                || response.errorCode === 'VerifyPlayerCodeFailed'
            ) {
                throw new ValidationError({
                    otp: [response.errorCode],
                    notice: [response.notice],
                });
            }
            throw NetworkUtils.getRequestError(response);
        }

        return response;
    }

    public async sendOtpCode(payload: SendMfaCodePayload): Promise<SendMfaCodeResponse> {
        const response = await this.restService.sendOtpCode(payload);

        if (response.error) {
            throw NetworkUtils.getRequestError(response);
        }

        return response;
    }

    public async getSessionStats(): Promise<SessionStats> {
        const response = await this.restService.getSessionStats();
        if (response.error) {
            throw NetworkUtils.getRequestError(response);
        }

        return {
            stakeAmount: response.data.stake_amount,
            winLossAmount: response.data.win_loss_amount,
        };
    }

    public async completeKycReverification(userId: string): Promise<void> {
        try {
            await this.restService.completeKycReverification(userId);
        } catch (error) {
            throw NetworkUtils.getRequestError(error);
        }
    }
}

function isBetlerAuthenticationError(error: any): boolean {
    return error?.type === RestClientErrorType.AuthenticationNeeded;
}

export const authenticationService = new AuthenticationService();
